import { Link } from "@tanstack/react-router"
import { useEffect, useState } from "react"
import { type QueryKey, useQueryClient } from "@tanstack/react-query"
import { ImSpinner2 } from "react-icons/im"
import { LuCalendarPlus, LuClock, LuMapPin, LuUsers } from "react-icons/lu"
import { RichText } from "~/components/ui/rich-text"
import { Drawer, DrawerContent, DrawerTitle } from "@mylaunch/components/ui"
import { Button } from "~/components/ui/button"
import { DrawerCover } from "~/components/ui/drawer-cover"
import { useAppMutation } from "~/hooks/useAppMutation"
import type { Event } from "@apps/gwservice/types"
import { toggleAttend } from "../server/calendar.functions"
import { shareEvent } from "../lib/share-event"
import { usePushPromptToast } from "~/features/notifications/hooks/use-push-prompt"

type BodyProps = {
  event: Event
  time: string
  attend?: "go" | "requested"
  loading: boolean
  onToggle: () => void
  onShare: () => void
}

function Body({ event, time, attend, loading, onToggle, onShare }: BodyProps) {
  return (
    <>
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col">
        {event.cover && <DrawerCover imageUrl={event.cover.url} mimeType={event.cover.mimeType} posterUrl={event.cover.poster?.url} />}
        <div className="flex flex-col gap-s p-m pb-[calc(2*var(--spacing-m)+3rem+env(safe-area-inset-bottom,0px))]">
          <div lang="ru" className="font-bold text-xl hyphens-auto">
            {event.title}
          </div>
          <div className="flex items-center gap-xs text-secondary">
            <LuUsers className="size-icon-s shrink-0" />
            {event.group ? (
              <Link to={"/group/" + event.group.id} className="truncate">{event.group.name}</Link>
            ) : (
              <span className="truncate">{"<УДАЛЕНО>"}</span>
            )}
          </div>
          <div className="flex items-center gap-xs text-muted-foreground">
            <LuClock className="size-icon-s shrink-0" />
            <span>{time}</span>
          </div>
          {event.location && (
            <div className="flex items-center gap-xs text-muted-foreground">
              <LuMapPin className="size-icon-s shrink-0" />
              <span>{event.location}</span>
            </div>
          )}
          {event.description && <RichText data={event.description} />}
        </div>
      </div>
      <div className="absolute inset-x-m bottom-[calc(env(safe-area-inset-bottom,0px)+var(--spacing-m))] flex items-center gap-s">
        <Button size="lg" className="flex-1" variant="default" disabled={loading} onClick={onToggle}>
          {loading ? <ImSpinner2 className="animate-spin" /> : attend === "go" ? "Вы идёте ✓" : attend === "requested" ? "Отменить заявку" : "Хочу пойти"}
        </Button>
        {attend === "go" && (
          <Button size="icon" variant="ghost" aria-label="В календарь" onClick={onShare}>
            <LuCalendarPlus className="size-icon-s" />
          </Button>
        )}
      </div>
    </>
  )
}

interface Props {
  event: Event | null
  onClose: () => void
  queryKey?: QueryKey
}

export function EventSheet({ event, onClose, queryKey }: Props) {
  const queryClient = useQueryClient()
  const [attend, setAttend] = useState<"go" | "requested" | undefined>(undefined)
  const pushPrompt = usePushPromptToast()
  useEffect(() => {
    setAttend(event?.attend)
  }, [event?.id])

  const toggleAttendMutation = useAppMutation<{ attend?: "go" | "requested" } | null, { eventId: number }>({
    mutationFn: ({ eventId }) => toggleAttend({ data: { eventId } }),
    errorMessage: "Не удалось обновить статус",
    onSuccess: (value) => {
      if (!value) return
      setAttend(value.attend)
      void queryClient.invalidateQueries({ queryKey: ["events"] })
      if (queryKey) {
        void queryClient.invalidateQueries({ queryKey })
      }
      if (!!value.attend) pushPrompt.offer()
    },
  })
  const time = event?.date.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })

  const handleToggle = () => {
    if (!event) return
    toggleAttendMutation.mutate({ eventId: event.id })
  }

  const handleShare = () => {
    if (event) void shareEvent(event)
  }

  return (
    <Drawer open={!!event} onOpenChange={(open) => !open && onClose()}>
      <DrawerContent
        handle={false}
        showCloseButton={!event?.cover}
        className="rounded-t-xl p-0 gap-0 border-0 outline-none"
        style={{ paddingBottom: 0 }}
      >
        <DrawerTitle className="sr-only">{event?.title}</DrawerTitle>
        {event && (
          <Body
            event={event}
            time={time!}
            attend={attend}
            loading={toggleAttendMutation.isPending}
            onToggle={handleToggle}
            onShare={handleShare}
          />
        )}
      </DrawerContent>
    </Drawer>
  )
}
